import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";

import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Drawer from "@mui/material/Drawer";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "tss-react/mui";
import CircularProgress from "@mui/material/CircularProgress";

import { setPrevState } from "../../../util/common";
import i18next from "../../../i18n";
import { localesLookup } from "./localesLookup";

import { hideHelp } from "../../../store/actions/helpPanel";

const useStyles = makeStyles()((theme) => ({
  Header: {
    display: "flex",
    alignItems: "center",
    fontSize: "1rem",
    "& p": {
      marginBottom: 0,
    },
  },
  body: {
    fontSize: "14px",
    "& p": {
      marginBottom: 0,
    },
  },
}));

const HelpBar = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { open, screen } = useSelector((state) => state.helpPanel);
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [helpDetails, setHelpDetails] = useState({
    header: "",
    body: "",
    footer: "",
  });

  const handleClose = useCallback(() => dispatch(hideHelp()), [dispatch]);

  const loadNamespace = useCallback(
    async (namespace = "translation") => {
      // Load Namespace dynamically if not loaded
      await i18next.loadNamespaces(namespace);
      await i18next.loadNamespaces("translation");

      // Namespace exists in current language, return it
      const namespaceExists = i18next.hasResourceBundle(i18next.language, namespace);
      if (namespaceExists) return i18next.getResourceBundle(i18next.language, namespace);

      // Namespace exists in fallback language, return it
      const namespaceFallbackExists = i18next.hasResourceBundle(i18next.options.fallbackLng, namespace);
      if (namespaceFallbackExists) return i18next.getResourceBundle(i18next.options.fallbackLng, namespace);

      // translation (default) exist in current language, return it
      const translationExists = i18next.hasResourceBundle(i18next.language, "translation");
      if (translationExists) return i18next.getResourceBundle(i18next.language, "translation");

      // translation (default) exist in fallback language, return it
      const translationFallbackExists = i18next.hasResourceBundle(i18next.options.fallbackLng, "translation");
      if (translationFallbackExists) return i18next.getResourceBundle(i18next.options.fallbackLng, "translation");

      //No help available so close the help panel
      handleClose();
    },
    [handleClose]
  );

  useEffect(() => {
    return handleClose;
  }, [handleClose, location.key]);

  useEffect(() => {
    const loadHelpData = async () => {
      let response = null;
      if (open) {
        setLoading(true);
        // load help data based on namespace or "translation" namespace (default)
        let namespace = localesLookup[screen] ? localesLookup[screen] : "translation";
        response = await loadNamespace(namespace);
        setPrevState(setHelpDetails, {
          screen: screen || null,
          header: response.Header || "",
          body: response.Body || "",
          footer: response.Footer || "",
        });
        setLoading(false);
      }
    };

    loadHelpData();

    setHelpDetails({
      header: "",
      body: "",
      footer: "",
    });
  }, [open, screen, loadNamespace]);

  return (
    <Drawer
      sx={{
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: "100%",
          maxWidth: "400px",
          position: "fixed",
          top: "45px",
          zIndex: (theme) => {
            return theme.zIndex.drawer;
          },
        },
      }}
      variant="persistent"
      anchor="right"
      open={open || false}
      onClose={handleClose}
    >
      {loading ? (
        <>
          <Divider />
          {/* header */}
          <Toolbar
            style={{ padding: "10px 15px 10px 15px", display: "flex", alignItems: "center" }}
            className={classes.Header}
          />
          <Divider />
          <Box sx={{ height: "100%", width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress color="primary" />
          </Box>
        </>
      ) : (
        <>
          <Divider />
          {/* header */}
          <Toolbar
            style={{ padding: "10px 15px 10px 15px", display: "flex", alignItems: "center" }}
            className={classes.Header}
          >
            <div
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(helpDetails.header, { ADD_ATTR: ["target"] }) }}
              style={{ display: "flex", alignContent: "center" }}
            />
            <IconButton onClick={handleClose} sx={{ marginLeft: "auto" }}>
              <CloseIcon />
            </IconButton>
          </Toolbar>

          <Divider />
          <Box sx={{ overflow: "auto", wordBreak: "break-word" }} className={classes.body}>
            {/* Body */}
            <Box sx={{ padding: "10px 15px 10px 15px", display: "flex", alignItems: "center" }}>
              <div
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(helpDetails.body, { ADD_ATTR: ["target"] }) }}
              />
            </Box>
            <Box sx={{ height: "50px" }}></Box>

            {/* footer */}
            {helpDetails.footer !== "" && (
              <>
                <Divider />
                <Box sx={{ padding: "10px 15px 10px 15px", display: "flex", alignItems: "center" }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(helpDetails.footer, { ADD_ATTR: ["target"] }),
                    }}
                    style={{ height: "100%", width: "100%" }}
                  />
                </Box>
                <Divider />
              </>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default HelpBar;
